import React from 'react'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import Badge from '@/components/ui/Badge'
import type { KegiatanPeserta } from '@/types/pocketbase'
import type { PresensiRecord } from '@/hooks/usePresensi'

interface PesertaRowProps {
  peserta: KegiatanPeserta
  presensi?: PresensiRecord
  kegiatanId: string
  onAttendanceChange?: (status: 'hadir' | 'alpha' | 'izin') => void
}

const paymentVariant: Record<string, 'green' | 'yellow' | 'red' | 'gray'> = {
  lunas: 'green',
  menunggu_verifikasi: 'yellow',
  belum_bayar: 'red',
  batal: 'gray',
}

const paymentLabel: Record<string, string> = {
  lunas: 'Lunas',
  menunggu_verifikasi: 'Menunggu',
  belum_bayar: 'Belum Bayar',
  batal: 'Batal',
}

function getInitials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

function Avatar({ name }: { name: string }) {
  return (
    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
      <span className="text-xs font-bold text-primary">{getInitials(name)}</span>
    </div>
  )
}

export default function PesertaRow({
  peserta,
  presensi,
  kegiatanId,
  onAttendanceChange,
}: PesertaRowProps) {
  const navigate = useNavigate()

  const statusBadge = presensi ? (
    presensi.status === 'hadir' ? (
      <div className="w-7 h-7 rounded-full bg-success flex items-center justify-center" title="Hadir">
        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    ) : presensi.status === 'alpha' ? (
      <div className="w-7 h-7 rounded-full bg-error flex items-center justify-center" title="Alpha">
        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
    ) : (
      <div className="w-7 h-7 rounded-full bg-warning flex items-center justify-center" title="Izin">
        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    )
  ) : (
    <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center" title="Belum absen">
      <span className="text-xs text-text-secondary">?</span>
    </div>
  )

  return (
    <div
      className="flex items-center gap-3 py-3 px-2 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
      onClick={() => navigate(`/kegiatan/${kegiatanId}/peserta/${peserta.id}`)}
    >
      <Avatar name={peserta.nama} />

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-text-primary truncate">{peserta.nama}</p>
        <div className="flex items-center gap-2 flex-wrap">
          {peserta.nbm && <span className="text-xs text-text-secondary">NBM: {peserta.nbm}</span>}
          {(peserta.expand?.daerah_id as { nama?: string })?.nama && (
            <span className="text-xs text-text-secondary">
              {(peserta.expand?.daerah_id as { nama?: string }).nama}
            </span>
          )}
          {(peserta.expand?.jabatan_id as { nama?: string })?.nama && (
            <span className="text-xs text-text-secondary">
              — {(peserta.expand?.jabatan_id as { nama?: string }).nama}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <Badge variant={paymentVariant[peserta.status_pembayaran]}>
          {paymentLabel[peserta.status_pembayaran]}
        </Badge>

        {onAttendanceChange ? (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => onAttendanceChange('hadir')}
              className={clsx(
                'w-7 h-7 rounded-full flex items-center justify-center transition-all',
                presensi?.status === 'hadir'
                  ? 'bg-success text-white'
                  : 'bg-gray-100 text-gray-400 hover:bg-success/20'
              )}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </button>
            <button
              onClick={() => onAttendanceChange('alpha')}
              className={clsx(
                'w-7 h-7 rounded-full flex items-center justify-center transition-all',
                presensi?.status === 'alpha'
                  ? 'bg-error text-white'
                  : 'bg-gray-100 text-gray-400 hover:bg-error/20'
              )}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <button
              onClick={() => onAttendanceChange('izin')}
              className={clsx(
                'w-7 h-7 rounded-full flex items-center justify-center transition-all',
                presensi?.status === 'izin'
                  ? 'bg-warning text-white'
                  : 'bg-gray-100 text-gray-400 hover:bg-warning/20'
              )}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
        ) : (
          statusBadge
        )}

        {presensi?.ttd_file && (
          <div className="text-success" title="Tanda tangan tersedia">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </div>
        )}
      </div>
    </div>
  )
}
